import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Stack,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { CheckCircle, Info, Close, Star, TrendingUp, LocalOffer } from '@mui/icons-material';

const features = [
  { name: 'Comprehensive career analysis', free: true, premium: true },
  { name: 'Career advancements recommendations', free: true, premium: true },
  { name: 'Bureau of Labor Statistics field analysis', free: true, premium: true },
  { name: 'Potential career transition analysis', free: true, premium: true },
  { name: 'Career history management', free: true, premium: true },
  { name: 'Prompted career history refinement', free: true, premium: true },
  { name: 'Smart accomplishment logging', free: true, premium: true },
  { name: 'Job qualification evaluations', free: '3 per month', premium: 'Unlimited' },
  { name: 'Resume generations', free: '1 per week', premium: 'Unlimited' },
  { name: 'Cover letter generations per job', free: '1', premium: 'Unlimited' },
  { name: 'Interview preparation (coming soon)', free: 'Limited', premium: 'Unlimited' },
  { name: 'Performance evaluation assistance (coming soon)', free: 'Limited', premium: 'Unlimited' },
  { name: 'Salary/Role negotiation assistance (coming soon)', free: 'Limited', premium: 'Unlimited' }
];

const pricingOptions = [
  {
    name: 'Federal Workers',
    badge: 'Limited Time',
    badgeType: 'limited',
    originalPrice: null,
    price: '$5',
    period: '/month',
    description: 'Special pricing for current and former federal employees/contractors.',
    linkedinRequired: true
  },
  {
    name: 'Weekly Plan',
    badge: 'Highest Flexibility',
    badgeType: 'beta',
    originalPrice: '$7',
    price: '$5',
    period: '/week',
    description: 'Premium access for performance reviews, salary negotiations, career transitions, or other short-term needs.'
  },
  {
    name: 'Monthly Plan',
    badge: 'Best Value',
    badgeType: 'beta',
    originalPrice: '$30',
    price: '$12',
    period: '/month',
    description: 'Highest value plan for comprehensive career management.',
    // savings: 'Save $18 / month'
  }
];

function Pricing() {
  const [mounted, setMounted] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const tableVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, rotateX: 10 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const priceVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        delay: 0.3,
        type: "spring",
        stiffness: 200,
      },
    },
  };

  if (!mounted) return null;

  return (
    <Box className="section-padding parallax-container" sx={{ backgroundColor: 'var(--color-neutral-50)', position: 'relative' }}>
      <div className="parallax-bg" />

      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <Stack spacing={10} alignItems="center">
            <motion.div variants={headerVariants}>
              <Box textAlign="center" sx={{ maxWidth: '700px' }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'var(--color-brand-primary)',
                    fontWeight: 700,
                    letterSpacing: 2,
                    fontSize: '0.875rem',
                    mb: 2,
                    display: 'block',
                  }}
                >
                  FREE TO USE
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark), var(--color-accent-primary))',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 6s ease infinite',
                    mb: 3,
                    fontWeight: 700,
                  }}
                >
                  Powerful Career Tools, Completely Free
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'var(--color-neutral-600)',
                    fontSize: '1.25rem',
                    lineHeight: 1.7,
                  }}
                >
                  Kazi is built to help everyone advance their career—not just those who can afford premium tools. Get comprehensive career analysis, resume generation, cover letters, and more without paying a dime. Optional premium upgrades are available for power users who want unlimited access.
                </Typography>
              </Box>
            </motion.div>

            {/* Free Value Highlight */}
            <motion.div variants={tableVariants} style={{ width: '100%' }}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05))',
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: 4,
                  p: 4,
                  mb: 6,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: '#22c55e',
                    fontWeight: 700,
                    mb: 2,
                    fontSize: '1.75rem',
                  }}
                >
                  ✨ Free Forever — No Credit Card Required
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'var(--color-neutral-700)',
                    fontSize: '1.1rem',
                    maxWidth: '600px',
                    mx: 'auto',
                  }}
                >
                  Every core feature is available on the free plan. Generate resumes, get career insights, create cover letters, and track your accomplishments—all without paying.
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  color: 'var(--color-neutral-900)',
                  mb: 4,
                  textAlign: 'center',
                  fontSize: '2rem',
                  fontWeight: 600,
                }}
              >
                What You Get for Free
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 49, 48, 0.1)',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{
                      background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                    }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'white' }}>
                        Features
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'white' }}>
                        Free Plan
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'white' }}>
                        Premium Plans
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {features.map((feature, index) => (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        transition={{ duration: 0.5, delay: 0.1 * index }}
                        style={{
                          background: index % 2 === 0 ? 'transparent' : 'rgba(255, 49, 48, 0.02)',
                        }}
                      >
                        <TableCell component="td" sx={{ fontWeight: 500, py: 2 }}>
                          {feature.name}
                        </TableCell>
                        <TableCell align="center">
                          {typeof feature.free === 'boolean' ? (
                            feature.free ? (
                              <CheckCircle sx={{ color: 'var(--color-brand-primary)', fontSize: 24 }} />
                            ) : (
                              <Close sx={{ color: 'var(--color-neutral-400)', fontSize: 24 }} />
                            )
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feature.free}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {typeof feature.premium === 'boolean' ? (
                            feature.premium ? (
                              <CheckCircle sx={{ color: 'var(--color-brand-primary)', fontSize: 24 }} />
                            ) : (
                              <Close sx={{ color: 'var(--color-neutral-400)', fontSize: 24 }} />
                            )
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-brand-primary)' }}>
                              {feature.premium}
                            </Typography>
                          )}
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </motion.div>

            {/* Optional Premium Upgrades */}
            <Box sx={{ width: '100%' }}>
              <motion.div variants={headerVariants}>
                <Typography
                  variant="h3"
                  sx={{
                    color: 'var(--color-neutral-900)',
                    mb: 2,
                    textAlign: 'center',
                    fontSize: '2rem',
                    fontWeight: 600,
                  }}
                >
                  Want Unlimited Access?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'var(--color-neutral-600)',
                    textAlign: 'center',
                    mb: 6,
                    fontSize: '1.1rem',
                  }}
                >
                  Upgrade to premium when you need unlimited resume generations, job analyses, and cover letters.
                </Typography>
              </motion.div>

              <Grid container spacing={4} justifyContent="center">
                {pricingOptions.map((option, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <motion.div
                      variants={cardVariants}
                      whileHover={{
                        y: -12,
                        scale: 1.02,
                        transition: { duration: 0.3 }
                      }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          position: 'relative',
                          border: '1px solid rgba(255, 49, 48, 0.1)',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                          backdropFilter: 'blur(20px)',
                          borderRadius: 4,
                          overflow: 'hidden',
                          transform: 'scale(1)',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                          '&:hover': {
                            boxShadow: '0 30px 60px rgba(255, 49, 48, 0.15)',
                            border: '1px solid rgba(255, 49, 48, 0.3)',
                          },
                        }}
                      >


                        <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Stack spacing={3} sx={{ flex: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Typography
                                variant="h4"
                                sx={{
                                  color: 'var(--color-neutral-900)',
                                  fontSize: '1.5rem',
                                  fontWeight: 700,
                                }}
                              >
                                {option.name}
                              </Typography>
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Chip
                                  label={option.badge}
                                  size="small"
                                  className={option.badgeType === 'beta' ? 'beta-badge' : 'limited-time-badge'}
                                  icon={option.badgeType === 'beta' ? <LocalOffer /> : <TrendingUp />}
                                />
                              </motion.div>
                            </Stack>

                            <motion.div variants={priceVariants}>
                              <Stack direction="row" alignItems="baseline" spacing={1} mb={2}>
                                {option.originalPrice && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textDecoration: 'line-through',
                                      color: 'var(--color-neutral-400)',
                                      fontSize: '1.1rem',
                                    }}
                                  >
                                    {option.originalPrice}
                                  </Typography>
                                )}
                                <Typography
                                  variant="h3"
                                  sx={{
                                    background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontWeight: 800,
                                    fontSize: '2.5rem',
                                  }}
                                >
                                  {option.price}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: 'var(--color-neutral-600)', fontWeight: 500 }}
                                >
                                  {option.period}
                                </Typography>
                              </Stack>
                            </motion.div>

                            <Box>
                              {option.savings && <Chip
                                label={option.savings}
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-dark))',
                                  color: 'var(--color-brand-dark)',
                                  fontWeight: 600,
                                  mb: 2,
                                }}
                              />}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'var(--color-brand-primary)',
                                  fontWeight: 600,
                                  lineHeight: 1.5,
                                }}
                              >
                                {option.description}
                              </Typography>
                            </Box>
                          </Stack>

                          {/* Floating Animation Elements */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 20,
                              right: 20,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: 'var(--color-brand-primary)30',
                              animation: 'floating 3s ease-in-out infinite',
                              opacity: 0.6,
                            }}
                          />
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <Box textAlign="center" sx={{ maxWidth: '600px' }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'var(--color-neutral-600)',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    background: 'linear-gradient(135deg, rgba(255, 49, 48, 0.05), rgba(255, 224, 92, 0.05))',
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid rgba(255, 49, 48, 0.1)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  To subscribe to a premium plan, create a free account and click "Subscribe" in the user menu.
                </Typography>
              </Box>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

export default Pricing; 